import ExcelJS from "exceljs";

const PLATFORM_NAME = process.env.REACT_APP_PROJECT_NAME || "AGCX";
const BRAND_NAME = "Arab Global Crypto Exchange";

const GOLD = "D1AA67";
const GOLD_SOFT = "F8F1E3";
const GOLD_BAND = "EFE0C0";
const INK = "1E2329";
const MUTED = "707A8A";
const HEADER_BG = "1E2329";
const HEADER_FG = "FFFFFF";
const ROW_WHITE = "FFFFFF";
const ALT_ROW = "F7F4EC";
const BORDER = "E6E8EB";
const SUCCESS = "0ECB81";
const SUCCESS_BG = "E8FBF3";
const DANGER = "F6465D";
const DANGER_BG = "FDECEF";
const WARN = "F0B90B";
const WARN_BG = "FFF8E6";

async function loadLogoAsBase64Png(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const max = 220;
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: canvas.width,
          height: canvas.height,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function resolveBrandLogo() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const candidates = [
    `${origin}/images/logo_light.svg`,
    `${origin}/images/favicon_light.svg`,
    `${origin}/logo192.png`,
    `${origin}/images/logo.png`,
  ];
  for (const url of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const logo = await loadLogoAsBase64Png(url);
    if (logo?.dataUrl) return logo;
  }
  return null;
}

function dataUrlToArrayBuffer(dataUrl) {
  const base64 = String(dataUrl || "").split(",")[1] || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function thinBorder() {
  return {
    top: { style: "thin", color: { argb: `FF${BORDER}` } },
    left: { style: "thin", color: { argb: `FF${BORDER}` } },
    bottom: { style: "thin", color: { argb: `FF${BORDER}` } },
    right: { style: "thin", color: { argb: `FF${BORDER}` } },
  };
}

function solidFill(hex) {
  return { type: "pattern", pattern: "solid", fgColor: { argb: `FF${hex}` } };
}

function paintRow(sheet, rowIndex, colCount, hex) {
  const row = sheet.getRow(rowIndex);
  for (let c = 1; c <= colCount; c += 1) {
    row.getCell(c).fill = solidFill(hex);
  }
}

function statusTone(raw) {
  const status = String(raw || "").toUpperCase();
  if (
    status.includes("EXECUTED") ||
    status.includes("SUCCESS") ||
    status.includes("COMPLETED") ||
    status.includes("FILLED") ||
    status.includes("PROCESSED") ||
    status.includes("REFUNDED")
  ) {
    return { fg: SUCCESS, bg: SUCCESS_BG };
  }
  if (
    status.includes("FAIL") ||
    status.includes("REJECT") ||
    status.includes("CANCEL") ||
    status.includes("REVERS") ||
    status.includes("NOT ACCEPTED")
  ) {
    return { fg: DANGER, bg: DANGER_BG };
  }
  if (
    status.includes("PENDING") ||
    status.includes("REVIEW") ||
    status.includes("AWAITING") ||
    status.includes("SUBMIT") ||
    status.includes("INITIAT") ||
    status.includes("QUOTE") ||
    status.includes("HOLD")
  ) {
    return { fg: WARN, bg: WARN_BG };
  }
  return { fg: INK, bg: null };
}

/**
 * Shared AGCE branded history workbook.
 * @param {{
 *   reportTitle: string,
 *   sheetName: string,
 *   fileSlug: string,
 *   subtitle?: string,
 *   columns: Array<{ key: string, header: string, width?: number, align?: string, tone?: 'status'|'money' }>,
 *   rows: Array<object>,
 *   meta?: { timeLabel?: string, statusLabel?: string }
 * }} opts
 */
export async function exportBrandedHistoryExcel(opts) {
  const {
    reportTitle,
    sheetName,
    fileSlug,
    subtitle = "Official statement export",
    columns,
    rows,
    meta = {},
  } = opts || {};

  const cols = Array.isArray(columns) ? columns : [];
  const list = Array.isArray(rows) ? rows : [];
  const colCount = Math.max(1, cols.length);
  const timeLabel = meta.timeLabel || "All time";
  const statusLabel = meta.statusLabel || "All status";
  const exportedAt = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = PLATFORM_NAME;
  workbook.created = new Date();
  workbook.company = BRAND_NAME;

  const sheet = workbook.addWorksheet(sheetName || "History", {
    views: [{ state: "frozen", ySplit: 9, showGridLines: false }],
    properties: { defaultRowHeight: 20 },
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    },
  });

  sheet.columns = cols.map((c) => ({
    key: c.key,
    width: c.width || 18,
  }));

  const accent = sheet.getRow(1);
  accent.height = 10;
  paintRow(sheet, 1, colCount, GOLD);
  for (let r = 2; r <= 7; r += 1) paintRow(sheet, r, colCount, GOLD_SOFT);

  let logoPlaced = false;
  try {
    const logo = await resolveBrandLogo();
    if (logo?.dataUrl) {
      const imageId = workbook.addImage({
        buffer: dataUrlToArrayBuffer(logo.dataUrl),
        extension: "png",
      });
      const aspect = logo.height > 0 ? logo.width / logo.height : 2;
      const logoH = 42;
      const logoW = Math.min(110, Math.round(logoH * aspect));
      sheet.addImage(imageId, {
        tl: { col: 0.2, row: 1.35 },
        ext: { width: logoW, height: logoH },
      });
      logoPlaced = true;
    }
  } catch {
    /* optional */
  }

  const brandStartCol = logoPlaced ? 2 : 1;
  sheet.mergeCells(2, brandStartCol, 2, colCount);
  const brandRow = sheet.getRow(2);
  brandRow.height = 26;
  brandRow.getCell(brandStartCol).value = BRAND_NAME;
  brandRow.getCell(brandStartCol).font = {
    name: "Calibri",
    size: 18,
    bold: true,
    color: { argb: `FF${INK}` },
  };
  brandRow.getCell(brandStartCol).alignment = { vertical: "middle", horizontal: "left" };

  sheet.mergeCells(3, brandStartCol, 3, colCount);
  const codeRow = sheet.getRow(3);
  codeRow.height = 18;
  codeRow.getCell(brandStartCol).value = PLATFORM_NAME;
  codeRow.getCell(brandStartCol).font = {
    name: "Calibri",
    size: 11,
    bold: true,
    color: { argb: `FF${GOLD}` },
  };

  sheet.mergeCells(4, 1, 4, colCount);
  const titleRow = sheet.getRow(4);
  titleRow.height = 24;
  titleRow.getCell(1).value = reportTitle || "History";
  titleRow.getCell(1).font = { name: "Calibri", size: 14, bold: true, color: { argb: `FF${INK}` } };
  titleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
  titleRow.getCell(1).border = {
    bottom: { style: "medium", color: { argb: `FF${GOLD}` } },
  };

  const mid = Math.max(2, Math.floor(colCount / 2));
  sheet.mergeCells(5, 1, 5, Math.min(3, colCount));
  if (colCount >= 4) sheet.mergeCells(5, 4, 5, Math.min(mid + 1, colCount));
  if (colCount >= 6) sheet.mergeCells(5, Math.min(mid + 2, colCount), 5, colCount);
  const metaRow = sheet.getRow(5);
  metaRow.height = 20;
  metaRow.getCell(1).value = `Exported  ${exportedAt}`;
  metaRow.getCell(1).font = { name: "Calibri", size: 10, color: { argb: `FF${MUTED}` } };
  if (colCount >= 4) {
    metaRow.getCell(4).value = `Time  ${timeLabel}   ·   Status  ${statusLabel}`;
    metaRow.getCell(4).font = { name: "Calibri", size: 10, color: { argb: `FF${MUTED}` } };
  }
  if (colCount >= 6) {
    metaRow.getCell(Math.min(mid + 2, colCount)).value = `Records  ${list.length}`;
    metaRow.getCell(Math.min(mid + 2, colCount)).font = {
      name: "Calibri",
      size: 10,
      color: { argb: `FF${MUTED}` },
    };
  }

  paintRow(sheet, 6, colCount, GOLD_BAND);
  sheet.mergeCells(6, 1, 6, colCount);
  const noteRow = sheet.getRow(6);
  noteRow.height = 18;
  noteRow.getCell(1).value = subtitle;
  noteRow.getCell(1).font = { name: "Calibri", size: 9, italic: true, color: { argb: `FF${INK}` } };

  sheet.getRow(7).height = 8;
  paintRow(sheet, 7, colCount, ROW_WHITE);

  const headerRowIndex = 8;
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 24;
  cols.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: `FF${HEADER_FG}` } };
    cell.fill = solidFill(HEADER_BG);
    cell.alignment = { vertical: "middle", horizontal: col.align || "left" };
    cell.border = {
      top: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
      left: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
      bottom: { style: "medium", color: { argb: `FF${GOLD}` } },
      right: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
    };
  });

  if (list.length) {
    sheet.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: headerRowIndex + list.length, column: colCount },
    };
  }

  list.forEach((row, idx) => {
    const excelRow = sheet.getRow(headerRowIndex + 1 + idx);
    excelRow.height = 22;
    const zebra = idx % 2 === 1 ? ALT_ROW : ROW_WHITE;
    cols.forEach((col, i) => {
      const cell = excelRow.getCell(i + 1);
      const val = row[col.key];
      cell.value = val == null || val === "" ? "—" : val;
      cell.font = { name: "Calibri", size: 10, color: { argb: `FF${INK}` } };
      cell.alignment = {
        vertical: "middle",
        horizontal: col.align || "left",
        wrapText: col.align === "left" && i === colCount - 1,
      };
      cell.border = thinBorder();
      cell.fill = solidFill(zebra);

      if (col.tone === "money") {
        cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: `FF${INK}` } };
      }
      if (col.tone === "status") {
        const tone = statusTone(row.status || row.statusLabel || val);
        cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: `FF${tone.fg}` } };
        if (tone.bg) cell.fill = solidFill(tone.bg);
      }
    });
  });

  const afterData = headerRowIndex + list.length + 1;
  sheet.getRow(afterData).height = 8;

  const footerRow = afterData + 1;
  sheet.mergeCells(footerRow, 1, footerRow, colCount);
  const footer = sheet.getRow(footerRow);
  footer.height = 20;
  paintRow(sheet, footerRow, colCount, GOLD_SOFT);
  footer.getCell(1).value = `© ${new Date().getFullYear()} ${BRAND_NAME} (${PLATFORM_NAME}). Confidential — for account holder use only.`;
  footer.getCell(1).font = { name: "Calibri", size: 9, italic: true, color: { argb: `FF${MUTED}` } };

  const accentBottom = footerRow + 1;
  const bottomBar = sheet.getRow(accentBottom);
  bottomBar.height = 8;
  paintRow(sheet, accentBottom, colCount, GOLD);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${PLATFORM_NAME}_${fileSlug || "History"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

function last4Label(last4) {
  const s = String(last4 || "").trim();
  return s ? `•••• ${s}` : "—";
}

/** Deposit Fiat History — branded Excel */
export async function exportFiatDepositHistoryBrandedExcel(rows, meta = {}) {
  const list = (rows || []).map((row) => ({
    dateLabel: row.dateLabel || "—",
    grossDisplay: row.gross ? `${row.gross} ${row.currency || "AED"}`.trim() : "—",
    feeDisplay: `${row.fee || "0"} ${row.currency || "AED"}`.trim(),
    creditedDisplay: `${row.net || row.amount || "0"} ${row.currency || "AED"}`.trim(),
    fromIban: last4Label(row.last4),
    status: row.status,
    statusLabel: row.statusLabel || "—",
    reason: row.statusReason || "",
    reference: row.channelRef || row.id || "",
    wallet: row.wallet || "Spot",
  }));

  return exportBrandedHistoryExcel({
    reportTitle: "Deposit History",
    sheetName: "Deposit History",
    fileSlug: "Fiat_Deposit_History",
    subtitle: "Official statement export · Fiat AED deposits to Spot",
    columns: [
      { key: "dateLabel", header: "Date", width: 22, align: "left" },
      { key: "grossDisplay", header: "Gross", width: 16, align: "right", tone: "money" },
      { key: "feeDisplay", header: "Fee", width: 12, align: "right", tone: "money" },
      { key: "creditedDisplay", header: "Credited", width: 16, align: "right", tone: "money" },
      { key: "fromIban", header: "From IBAN", width: 14, align: "center" },
      { key: "statusLabel", header: "Status", width: 14, align: "center", tone: "status" },
      { key: "reason", header: "Status reason", width: 24, align: "left" },
      { key: "wallet", header: "Wallet", width: 10, align: "center" },
      { key: "reference", header: "Reference", width: 28, align: "left" },
    ],
    rows: list,
    meta,
  });
}

/** Withdrawals History — branded Excel */
export async function exportFiatWithdrawHistoryBrandedExcel(rows, meta = {}) {
  const list = (rows || []).map((row) => ({
    dateLabel: row.dateLabel || "—",
    amountDisplay: `${row.amount || "0"} ${row.currency || "AED"}`.trim(),
    feeDisplay: `${row.fee || "0"} ${row.currency || "AED"}`.trim(),
    netDisplay: `${row.net || "0"} ${row.currency || "AED"}`.trim(),
    bank: row.bank || "—",
    status: row.status,
    statusLabel: row.statusLabel || "—",
    reason: row.statusReason || "",
    wallet: row.wallet || "Spot",
    reference: row.id || "",
  }));

  return exportBrandedHistoryExcel({
    reportTitle: "Withdrawals History",
    sheetName: "Withdrawals",
    fileSlug: "Fiat_Withdrawals_History",
    subtitle: "Official statement export · Spot AED withdrawals to bank",
    columns: [
      { key: "dateLabel", header: "Date", width: 22, align: "left" },
      { key: "amountDisplay", header: "Amount", width: 14, align: "right", tone: "money" },
      { key: "feeDisplay", header: "Fee", width: 12, align: "right", tone: "money" },
      { key: "netDisplay", header: "Net", width: 14, align: "right", tone: "money" },
      { key: "bank", header: "Bank", width: 18, align: "left" },
      { key: "statusLabel", header: "Status", width: 14, align: "center", tone: "status" },
      { key: "reason", header: "Status reason", width: 22, align: "left" },
      { key: "wallet", header: "Wallet", width: 10, align: "center" },
      { key: "reference", header: "Reference", width: 28, align: "left" },
    ],
    rows: list,
    meta,
  });
}
